<template>
  <div>
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-start mb-1">
      <div>
        <h4 class="font-weight-bold">Bonus GGR by User</h4>
        <div class="text-muted">
          Track bonus-funded bet profitability at the user level
        </div>
      </div>

      <div>
        <b-button
          variant="primary"
          @click="handleExport"
          :disabled="loadingBonusGgrByUser"
        >
          <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
          Export
        </b-button>
      </div>
    </div>

    <!-- STATS -->
    <b-card>
      <div>
        <b-row>
          <b-col cols="12" sm="6" md="3">
            <div class="stat-box">
              <div class="text-muted small">Total Users</div>
              <div class="h5 font-weight-bold">{{ metadata.total || 0 }}</div>
            </div>
          </b-col>
          <b-col cols="12" sm="6" md="3">
            <div class="stat-box">
              <div class="text-muted small">Total Bonus Turnover</div>
              <div class="h5 font-weight-bold">
                ${{ totalBonusTurnover.toFixed(2) }}
              </div>
            </div>
          </b-col>
          <b-col cols="12" sm="6" md="3">
            <div class="stat-box">
              <div class="text-muted small">Total Bonus GGR</div>
              <div class="h5 font-weight-bold text-success">
                ${{ totalBonusGgr.toFixed(2) }}
              </div>
            </div>
          </b-col>
          <b-col cols="12" sm="6" md="3">
            <div class="stat-box">
              <div class="text-muted small">Avg Margin %</div>
              <div class="h5 font-weight-bold">
                {{ avgMarginPct.toFixed(2) }}%
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- TABLE -->
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6">
            <label>Show</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="d-inline-block mx-1"
              style="width: 100px"
            />
            <label>entries</label>
          </b-col>
          <b-col cols="12" md="6" class="text-right">
            <b-form-input v-model="search" placeholder="Search by email..." />
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="filteredItems"
        :fields="tableFields"
        show-empty
        responsive
        :busy="loadingBonusGgrByUser"
      >
        <template #cell(userId)="{ item }">
          <b-link :to="{ name: 'customers-view', params: { id: item.userId } }">
            {{ item.userId }}
          </b-link>
        </template>

        <template #cell(name)="{ item }">
          {{ item.firstName }} {{ item.lastName }}
        </template>

        <template #cell(bonusTurnover)="{ item }">
          <span class="font-weight-semibold">{{
            formatCurrencyBuiltInFunc(item.bonusTurnover)
          }}</span>
        </template>

        <template #cell(bonusGgr)="{ item }">
          <span
            class="font-weight-semibold"
            :class="item.bonusGgr > 0 ? 'text-success' : 'text-danger'"
          >
            {{ formatCurrencyBuiltInFunc(item.bonusGgr) }}
          </span>
        </template>

        <template #cell(bonusMarginPct)="{ item }">
          <b-badge :variant="item.bonusMarginPct > 0 ? 'success' : 'danger'">
            {{ item.bonusMarginPct.toFixed(2) }}%
          </b-badge>
        </template>
      </b-table>

      <div class="mx-2 mb-2 mt-2" v-if="metadata.total > 0">
        <b-row>
          <b-col cols="12" sm="6">
            <span class="text-muted">
              Showing {{ (currentPage - 1) * perPage + 1 }} to
              {{ Math.min(currentPage * perPage, metadata.total) }} of
              {{ metadata.total }} entries
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex justify-content-end"
            >
            <b-pagination
              v-model="currentPage"
              :total-rows="metadata.total"
              :per-page="perPage"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BLink,
  BBadge,
  BFormInput,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapState } from "vuex";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BLink,
    BBadge,
    BFormInput,
    vSelect,
    BPagination,
  },
  data() {
    return {
      currentPage: 1,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      search: "",
      tableFields: [
        { key: "userId", label: "User ID" },
        { key: "email", label: "Email" },
        { key: "name", label: "Name" },
        { key: "bonusTurnover", label: "Bonus Turnover" },
        { key: "bonusGgr", label: "Bonus GGR" },
        { key: "bonusMarginPct", label: "Margin %" },
      ],
      metadata: { total: 0 },
    };
  },
  computed: {
    ...mapState("reporting", [
      "bonusGgrByUser",
      "loadingBonusGgrByUser",
      "bonusGgrByUserPagination",
    ]),
    transformedData() {
      console.log(this.bonusGgrByUser);
      return this.transformData(this.bonusGgrByUser || []);
    },
    filteredItems() {
      if (!this.search) return this.transformedData;
      const s = this.search.toLowerCase();
      return this.transformedData.filter(
        (item) =>
          item.email?.toLowerCase().includes(s) ||
          item.userId?.toLowerCase().includes(s) ||
          item.firstName?.toLowerCase().includes(s) ||
          item.lastName?.toLowerCase().includes(s)
      );
    },
    totalBonusTurnover() {
      return (this.transformedData || []).reduce(
        (sum, item) => sum + (item.bonusTurnover || 0),
        0
      );
    },
    totalBonusGgr() {
      return (this.transformedData || []).reduce(
        (sum, item) => sum + (item.bonusGgr || 0),
        0
      );
    },
    avgMarginPct() {
      const data = this.transformedData || [];
      if (data.length === 0) return 0;
      const total = data.reduce(
        (sum, item) => sum + (item.bonusTurnover || 0),
        0
      );
      if (total === 0) return 0;
      return (this.totalBonusGgr / total) * 100;
    },
  },
  watch: {
    currentPage() {
      this.fetchData();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchData();
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions("reporting", ["getBonusGgrByUser"]),
    formatCurrencyBuiltInFunc,
    async fetchData() {
      const data = await this.getBonusGgrByUser({
        page: this.currentPage,
        limit: this.perPage,
      });
      if (data?.pagination) {
        this.metadata = {
          total: data.pagination.total || 0,
        };
      }
    },
    transformData(items) {
      const a = items.map((item) => ({
        ...item,
        bonusTurnover: parseFloat(item.bonusTurnover) || 0,
        bonusGgr: parseFloat(item.bonusGgr) || 0,
        bonusMarginPct: parseFloat(item.bonusMarginPct) || 0,
      }));
      console.log(a);
      return a;
    },

    handleExport() {
      alert("Export functionality to be implemented");
    },
  },
};
</script>

<style scoped>
.stat-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}
</style>
